/**
 * Copyright (c) 2022–2026 Podstack.ai
 * All rights reserved.
 * Proprietary and Confidential.
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 * This file is part of the Podstack Platform.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  XMarkIcon,
  CubeIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import Header from '../components/layout/Header';
import ProjectModal from '../components/projects/ProjectModal';
import { dataVolumeApi } from '../services/vmApi';
import toast from 'react-hot-toast';
import { DataVolumeCreate, DataVolumeSourceType, DataVolumeAccessMode } from '../types/dataVolume';
import { useProjects } from '../hooks/useProjects';
import { Project } from '../types';

type Step = 'project' | 'basic' | 'storage' | 'source' | 'review';

const sourceTypes: { value: DataVolumeSourceType; label: string; description: string }[] = [
  { value: 'blank', label: 'Blank Disk', description: 'Create an empty disk volume' },
  { value: 'http', label: 'HTTP/HTTPS', description: 'Import from HTTP/HTTPS URL' },
  { value: 'registry', label: 'Container Registry', description: 'Import from container registry' },
  { value: 'pvc', label: 'Existing PVC', description: 'Clone from existing PersistentVolumeClaim' },
];

const accessModes: { value: DataVolumeAccessMode; label: string; description: string }[] = [
  { value: 'ReadWriteOnce', label: 'ReadWriteOnce (RWO)', description: 'Can be mounted as read-write by a single node' },
  { value: 'ReadWriteMany', label: 'ReadWriteMany (RWX)', description: 'Can be mounted as read-write by many nodes' },
  { value: 'ReadOnlyMany', label: 'ReadOnlyMany (ROX)', description: 'Can be mounted read-only by many nodes' },
];

const CreateDataVolume: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project');
  
  const [step, setStep] = useState<Step>(projectIdFromUrl ? 'basic' : 'project');
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  
  // Project selector state
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const projectDropdownRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState<DataVolumeCreate>({
    name: '',
    description: '',
    size_gb: 10,
    storage_class: '',
    access_mode: 'ReadWriteOnce',
    source_type: 'blank',
    source_url: undefined,
    source_pvc_name: undefined,
    source_pvc_namespace: undefined,
    project_id: projectIdFromUrl || undefined,
  });
  
  // Local state for input values to allow empty during typing
  const [sizeInputValue, setSizeInputValue] = useState<string>('10');
  
  const { projects, createProject, isCreating, refetch: refetchProjects } = useProjects();
  
  // Filter projects based on search query
  const filteredProjects = projects.filter((project: Project) =>
    project.name.toLowerCase().includes(projectSearchQuery.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target as Node)) {
        setIsProjectDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedProject = projects.find((p: Project) => p.id === formData.project_id);
  
  const steps: { id: Step; label: string }[] = [
    { id: 'project', label: 'Project' },
    { id: 'basic', label: 'Basic Info' },
    { id: 'storage', label: 'Storage' },
    { id: 'source', label: 'Source' },
    { id: 'review', label: 'Review' },
  ];
  
  const currentStepIndex = steps.findIndex((s) => s.id === step);
  
  const canGoNext = () => {
    switch (step) {
      case 'project':
        return !!formData.project_id;
      case 'basic':
        return !!formData.name.trim();
      case 'storage':
        return formData.size_gb >= 1;
      case 'source':
        if (formData.source_type === 'blank') return true;
        if (formData.source_type === 'http' || formData.source_type === 'registry') {
          return !!formData.source_url?.trim();
        }
        if (formData.source_type === 'pvc') {
          return !!formData.source_pvc_name?.trim();
        }
        return false;
      default:
        return true;
    }
  };
  
  const handleNext = () => {
    if (canGoNext()) {
      const nextIndex = currentStepIndex + 1;
      if (nextIndex < steps.length) {
        setStep(steps[nextIndex].id);
      }
    }
  };
  
  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setStep(steps[prevIndex].id);
    }
  };
  
  const createMutation = useMutation({
    mutationFn: (data: { data: Omit<DataVolumeCreate, 'project_id'>; projectId: string }) => 
      dataVolumeApi.createVolume(data.data, data.projectId),
    onSuccess: (response) => {
      toast.success('Data volume creation initiated');
      navigate(`/data-volumes?project=${formData.project_id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to create data volume');
    },
  });
  
  const handleCreate = () => {
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    
    if (!formData.name.trim()) {
      toast.error('Please enter a volume name');
      return;
    }
    
    if (formData.size_gb < 1) {
      toast.error('Size must be at least 1 GB');
      return;
    }
    
    // Validate source-specific fields
    if (formData.source_type === 'http' || formData.source_type === 'registry') {
      if (!formData.source_url?.trim()) {
        toast.error(`Source URL is required for ${formData.source_type} source type`);
        return;
      }
    }
    
    if (formData.source_type === 'pvc') {
      if (!formData.source_pvc_name?.trim()) {
        toast.error('Source PVC name is required');
        return;
      }
    }
    
    // Clean up undefined values
    // Note: project_id is passed as query parameter, not in body
    const cleanData: Omit<DataVolumeCreate, 'project_id'> = {
      name: formData.name.trim(),
      size_gb: formData.size_gb,
      access_mode: formData.access_mode,
      source_type: formData.source_type,
    };
    
    if (formData.description?.trim()) {
      cleanData.description = formData.description.trim();
    }
    
    if (formData.storage_class?.trim()) {
      cleanData.storage_class = formData.storage_class.trim();
    }
    
    if (formData.source_url?.trim()) {
      cleanData.source_url = formData.source_url.trim();
    }
    
    if (formData.source_pvc_name?.trim()) {
      cleanData.source_pvc_name = formData.source_pvc_name.trim();
    }
    
    if (formData.source_pvc_namespace?.trim()) {
      cleanData.source_pvc_namespace = formData.source_pvc_namespace.trim();
    }
    
    // Pass project_id separately as it's a query parameter
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    createMutation.mutate({ data: cleanData, projectId: formData.project_id });
  };
  
  const handleProjectSubmit = (data: { name: string; description: string }) => {
    createProject(data);
    setShowCreateProjectModal(false);
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        title="Create Data Volume"
        subtitle="Create a standalone disk volume"
        actions={
          <button
            onClick={() => navigate('/data-volumes')}
            className="btn-secondary flex items-center gap-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Cancel
          </button>
        }
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            {/* Progress Steps */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between">
                {steps.map((s, index) => (
                  <React.Fragment key={s.id}>
                    <div className="flex items-center">
                      <div
                        className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                          index <= currentStepIndex
                            ? 'bg-primary-600 border-primary-600 text-white'
                            : 'bg-white border-gray-300 text-gray-400'
                        }`}
                      >
                        {index < currentStepIndex ? (
                          <CheckCircleIcon className="w-6 h-6" />
                        ) : (
                          <span className="text-sm font-semibold">{index + 1}</span>
                        )}
                      </div>
                      <span
                        className={`ml-3 text-sm font-medium ${
                          index <= currentStepIndex ? 'text-gray-900' : 'text-gray-400'
                        }`}
                      >
                        {s.label}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-4 ${
                          index < currentStepIndex ? 'bg-primary-600' : 'bg-gray-300'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            
            {/* Form Content */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              {/* Project Selection Step */}
              {step === 'project' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Select Project</h2>
                    <p className="text-sm text-gray-500">
                      Choose the project where you want to create the data volume.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project <span className="text-red-500">*</span>
                    </label>
                    <div className="relative" ref={projectDropdownRef}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={projectSearchQuery}
                            onChange={(e) => {
                              setProjectSearchQuery(e.target.value);
                              setIsProjectDropdownOpen(true);
                            }}
                            onFocus={() => setIsProjectDropdownOpen(true)}
                            placeholder="Search projects..."
                            className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          {projectSearchQuery && (
                            <button
                              onClick={() => {
                                setProjectSearchQuery('');
                                setIsProjectDropdownOpen(true);
                              }}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              <XMarkIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => setShowCreateProjectModal(true)}
                          className="px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 transition-colors"
                        >
                          <PlusIcon className="w-5 h-5" />
                          New
                        </button>
                      </div>
                      
                      {isProjectDropdownOpen && (
                        <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                          {filteredProjects.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                              No projects found
                            </div>
                          ) : (
                            filteredProjects.map((project: Project) => (
                              <button
                                key={project.id}
                                onClick={() => {
                                  setFormData((prev) => ({ ...prev, project_id: project.id }));
                                  setProjectSearchQuery(project.name);
                                  setIsProjectDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                                  formData.project_id === project.id ? 'bg-primary-50' : ''
                                }`}
                              >
                                <div className="font-medium text-gray-900">{project.name}</div>
                                {project.description && (
                                  <div className="text-sm text-gray-500 mt-1">{project.description}</div>
                                )}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {selectedProject && (
                      <p className="mt-2 text-sm text-gray-500">
                        Selected: <span className="font-medium text-gray-900">{selectedProject.name}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Basic Info Step */}
              {step === 'basic' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Basic Information</h2>
                    <p className="text-sm text-gray-500">
                      Provide a name and optional description for your data volume.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Volume Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-.]/g, '');
                        setFormData((prev) => ({ ...prev, name: value }));
                      }}
                      placeholder="my-data-volume"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Lowercase letters, numbers, hyphens, and dots only. Must start and end with a letter or number.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Optional description for this data volume"
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              )}
              
              {/* Storage Configuration Step */}
              {step === 'storage' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Storage Configuration</h2>
                    <p className="text-sm text-gray-500">
                      Configure the size and access mode for your data volume.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Size (GB) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={sizeInputValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSizeInputValue(value);
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue >= 1) {
                          setFormData((prev) => ({ ...prev, size_gb: numValue }));
                        } else if (value === '') {
                          setFormData((prev) => ({ ...prev, size_gb: 1 }));
                        }
                      }}
                      onBlur={() => {
                        if (!sizeInputValue || parseFloat(sizeInputValue) < 1) {
                          setSizeInputValue('1');
                          setFormData((prev) => ({ ...prev, size_gb: 1 }));
                        }
                      }}
                      min="1"
                      step="1"
                      placeholder="10"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">Minimum: 1 GB</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Storage Class
                    </label>
                    <input
                      type="text"
                      value={formData.storage_class || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, storage_class: e.target.value }))}
                      placeholder="Leave empty to use default storage class"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Optional. Leave empty to use the cluster's default storage class.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Access Mode <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {accessModes.map((mode) => (
                        <label
                          key={mode.value}
                          className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                            formData.access_mode === mode.value
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="access_mode"
                            value={mode.value}
                            checked={formData.access_mode === mode.value}
                            onChange={(e) => setFormData((prev) => ({ ...prev, access_mode: e.target.value as DataVolumeAccessMode }))}
                            className="mt-1 mr-3"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{mode.label}</div>
                            <div className="text-sm text-gray-500 mt-1">{mode.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Source Configuration Step */}
              {step === 'source' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Source Configuration</h2>
                    <p className="text-sm text-gray-500">
                      Choose how to initialize your data volume.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Source Type <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {sourceTypes.map((type) => (
                        <label
                          key={type.value}
                          className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                            formData.source_type === type.value
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="source_type"
                            value={type.value}
                            checked={formData.source_type === type.value}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...prev,
                                source_type: e.target.value as DataVolumeSourceType,
                                source_url: undefined,
                                source_pvc_name: undefined,
                                source_pvc_namespace: undefined,
                              }));
                            }}
                            className="mt-1 mr-3"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{type.label}</div>
                            <div className="text-sm text-gray-500 mt-1">{type.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* HTTP/Registry Source URL */}
                  {(formData.source_type === 'http' || formData.source_type === 'registry') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Source URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        value={formData.source_url || ''}
                        onChange={(e) => setFormData((prev) => ({ ...prev, source_url: e.target.value }))}
                        placeholder={formData.source_type === 'http' ? 'https://example.com/image.qcow2' : 'docker://registry.example.com/image:tag'}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {formData.source_type === 'http' 
                          ? 'HTTP/HTTPS URL to download the disk image from'
                          : 'Container registry URL in format: docker://registry/image:tag'}
                      </p>
                    </div>
                  )}
                  
                  {/* PVC Source */}
                  {formData.source_type === 'pvc' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Source PVC Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.source_pvc_name || ''}
                          onChange={(e) => setFormData((prev) => ({ ...prev, source_pvc_name: e.target.value }))}
                          placeholder="existing-pvc-name"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Source PVC Namespace
                        </label>
                        <input
                          type="text"
                          value={formData.source_pvc_namespace || ''}
                          onChange={(e) => setFormData((prev) => ({ ...prev, source_pvc_namespace: e.target.value }))}
                          placeholder="default"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Optional. Leave empty to use the same namespace as the data volume.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* Review Step */}
              {step === 'review' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Review Configuration</h2>
                    <p className="text-sm text-gray-500">
                      Review your data volume configuration before creating.
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h3>
                      <dl className="space-y-2">
                        <div className="flex justify-between">
                          <dt className="text-sm text-gray-500">Name</dt>
                          <dd className="text-sm font-medium text-gray-900">{formData.name}</dd>
                        </div>
                        {formData.description && (
                          <div className="flex justify-between">
                            <dt className="text-sm text-gray-500">Description</dt>
                            <dd className="text-sm font-medium text-gray-900">{formData.description}</dd>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <dt className="text-sm text-gray-500">Project</dt>
                          <dd className="text-sm font-medium text-gray-900">{selectedProject?.name}</dd>
                        </div>
                      </dl>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Storage</h3>
                      <dl className="space-y-2">
                        <div className="flex justify-between">
                          <dt className="text-sm text-gray-500">Size</dt>
                          <dd className="text-sm font-medium text-gray-900">{formData.size_gb} GB</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm text-gray-500">Storage Class</dt>
                          <dd className="text-sm font-medium text-gray-900">{formData.storage_class || 'Default'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm text-gray-500">Access Mode</dt>
                          <dd className="text-sm font-medium text-gray-900">{formData.access_mode}</dd>
                        </div>
                      </dl>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Source</h3>
                      <dl className="space-y-2">
                        <div className="flex justify-between">
                          <dt className="text-sm text-gray-500">Source Type</dt>
                          <dd className="text-sm font-medium text-gray-900 capitalize">{formData.source_type}</dd>
                        </div>
                        {formData.source_url && (
                          <div className="flex justify-between">
                            <dt className="text-sm text-gray-500">Source URL</dt>
                            <dd className="text-sm font-medium text-gray-900 break-all">{formData.source_url}</dd>
                          </div>
                        )}
                        {formData.source_pvc_name && (
                          <>
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">Source PVC Name</dt>
                              <dd className="text-sm font-medium text-gray-900">{formData.source_pvc_name}</dd>
                            </div>
                            {formData.source_pvc_namespace && (
                              <div className="flex justify-between">
                                <dt className="text-sm text-gray-500">Source PVC Namespace</dt>
                                <dd className="text-sm font-medium text-gray-900">{formData.source_pvc_namespace}</dd>
                              </div>
                            )}
                          </>
                        )}
                      </dl>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={handleBack}
                  disabled={currentStepIndex === 0}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Back
                </button>
                {step === 'review' ? (
                  <button
                    onClick={handleCreate}
                    disabled={createMutation.isPending}
                    className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {createMutation.isPending ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <CubeIcon className="w-5 h-5" />
                        Create Data Volume
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    disabled={!canGoNext()}
                    className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Summary</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Volume Name</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {formData.name || <span className="text-gray-400">Not set</span>}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Size</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {formData.size_gb} GB
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Source Type</p>
                  <p className="text-sm font-medium text-gray-900 mt-1 capitalize">
                    {formData.source_type}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Access Mode</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {formData.access_mode}
                  </p>
                </div>
                {selectedProject && (
                  <div>
                    <p className="text-sm text-gray-500">Project</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      {selectedProject.name}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Create Project Modal */}
      {showCreateProjectModal && (
        <ProjectModal
          isOpen={showCreateProjectModal}
          onClose={() => setShowCreateProjectModal(false)}
          onSubmit={handleProjectSubmit}
          isSubmitting={isCreating}
        />
      )}
    </div>
  );
};

export default CreateDataVolume;
